import React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Alert,
    Avatar,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Skeleton,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CloseIcon from '@mui/icons-material/Close';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import moment from 'moment';
import { doGET, doPOST } from '../../utils/HttpUtils';
import { showError, showSuccess } from '../../services/toaster';
import { useCompanyStore } from '../../services/company';

type DeletedUser = {
    _id: string;
    name?: string;
    email?: string;
    corporateEmail?: string;
    phone?: string;
    deletedAt?: string;
    archivedAt?: string;
};

type ArchivedRecord = {
    _id: string;
    user: Record<string, unknown>;
    archivedAt?: string;
    archivedBy?: string;
};

const formatRecordValue = (value: unknown): string => {
    if (value === undefined || value === null || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

const pageSize = 10;

const DeletedUsers: React.FC = () => {
    const { globalCompanyId } = useCompanyStore();
    const [open, setOpen] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [tab, setTab] = React.useState(0);
    const [users, setUsers] = React.useState<DeletedUser[]>([]);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [loadError, setLoadError] = React.useState<string | null>(null);
    const [selected, setSelected] = React.useState<DeletedUser | null>(null);
    const [confirmation, setConfirmation] = React.useState('');
    const [archiving, setArchiving] = React.useState(false);
    const [detailId, setDetailId] = React.useState<string | null>(null);
    const [record, setRecord] = React.useState<ArchivedRecord | null>(null);
    const [detailLoading, setDetailLoading] = React.useState(false);
    const [detailError, setDetailError] = React.useState<string | null>(null);
    const listRequestId = React.useRef(0);

    const loadDeleted = React.useCallback(async () => {
        const requestId = ++listRequestId.current;
        setLoading(true);
        setLoadError(null);
        const params = new URLSearchParams({ page: String(page), limit: String(pageSize) });
        if (globalCompanyId && globalCompanyId !== 'general') {
            params.set('corporateId', globalCompanyId);
        }
        const response = await doGET(`/users/${tab === 0 ? 'deleted' : 'archived'}?${params.toString()}`);
        if (requestId !== listRequestId.current) return;
        if (response.status >= 200 && response.status < 300) {
            setUsers(response.data?.data?.users || []);
            setTotal(response.data?.data?.total || 0);
        } else {
            setUsers([]);
            setTotal(0);
            setLoadError(response.message || 'Failed to fetch users');
        }
        setLoading(false);
    }, [globalCompanyId, page, tab]);

    React.useEffect(() => {
        if (open) loadDeleted();
        else listRequestId.current += 1;
    }, [open, loadDeleted]);

    React.useEffect(() => {
        setPage(1);
    }, [globalCompanyId]);

    React.useEffect(() => {
        if (!detailId) return;
        let cancelled = false;
        setRecord(null);
        setDetailError(null);
        setDetailLoading(true);
        doGET(`/users/archived/${detailId}`).then((response) => {
            if (cancelled) return;
            if (response.status >= 200 && response.status < 300) {
                setRecord(response.data?.data?.record || null);
            } else {
                setDetailError(response.message || 'Failed to load archived record');
            }
            setDetailLoading(false);
        });
        return () => { cancelled = true; };
    }, [detailId]);

    const closeConfirmation = () => {
        if (archiving) return;
        setSelected(null);
        setConfirmation('');
    };

    const archiveUser = async () => {
        if (!selected || confirmation !== `ARCHIVE ${selected._id}` || archiving) return;
        setArchiving(true);
        const response = await doPOST(`/users/${selected._id}/archive`, { confirmation });
        setArchiving(false);
        if (response.status >= 200 && response.status < 300) {
            showSuccess('User moved to archive');
            setSelected(null);
            setConfirmation('');
            setPage(1);
            setTab(1);
        } else {
            showError(response.message || 'Failed to archive user');
        }
    };

    return (
        <>
            <Button variant="outlined" color="inherit" startIcon={<PersonOffOutlinedIcon />} onClick={() => setOpen(true)}>
                Deleted users
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth aria-labelledby="deleted-users-title">
                <DialogTitle id="deleted-users-title" sx={{ pb: 1 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Typography variant="h6" component="span" fontWeight={600}>Deleted users</Typography>
                            {!loading && !loadError && <Chip size="small" label={total} />}
                        </Stack>
                        <IconButton aria-label="Close deleted users" onClick={() => setOpen(false)} size="small"><CloseIcon /></IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
                    <Tabs
                        value={tab}
                        onChange={(_event, value: number) => { setTab(value); setPage(1); }}
                        aria-label="Deleted user status"
                        sx={{ mb: 2 }}
                    >
                        <Tab label="Pending archive" />
                        <Tab label="Archived" />
                    </Tabs>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                        {tab === 0
                            ? 'Soft-deleted users are hidden from active lists. Move one to the archive to remove it from the User collection while keeping its record.'
                            : 'Archived user records and their original IDs are retained here. Linked booking records remain.'}
                    </Typography>
                    {loadError ? (
                        <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadDeleted}>Retry</Button>}>
                            {loadError}
                        </Alert>
                    ) : loading ? (
                        <Stack spacing={1} aria-label="Loading deleted users">
                            {[0, 1, 2].map((item) => <Skeleton key={item} variant="rounded" height={60} />)}
                        </Stack>
                    ) : users.length === 0 ? (
                        <Stack alignItems="center" spacing={1} sx={{ py: 5, textAlign: 'center' }}>
                            <PersonOffOutlinedIcon color="disabled" sx={{ fontSize: 36 }} />
                            <Typography variant="subtitle1" fontWeight={600}>{tab === 0 ? 'No users pending archive' : 'No archived users'}</Typography>
                            <Typography variant="body2" color="text.secondary">{tab === 0 ? 'Soft-deleted users will appear here.' : 'Archived users will appear here after you move them from the pending list.'}</Typography>
                        </Stack>
                    ) : (
                        <TableContainer>
                            <Table size="small" aria-label={tab === 0 ? 'Users pending archive' : 'Archived users'} sx={{ minWidth: { sm: 640 } }}>
                                <TableHead sx={{ bgcolor: 'grey.50' }}>
                                    <TableRow>
                                        <TableCell>User</TableCell>
                                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Contact</TableCell>
                                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{tab === 0 ? 'Deleted on' : 'Archived on'}</TableCell>
                                        <TableCell align="right">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map((user) => (
                                        <TableRow key={user._id} hover>
                                            <TableCell sx={{ py: 1.5 }}>
                                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                                    <Avatar sx={{ width: 34, height: 34, fontSize: 14, bgcolor: 'grey.200', color: 'text.primary' }}>
                                                        {user.name?.charAt(0).toUpperCase() || '?'}
                                                    </Avatar>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography variant="body2" fontWeight={600} noWrap>{user.name || 'Unnamed user'}</Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
                                                            ID: {user._id}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: { sm: 'none' }, overflowWrap: 'anywhere' }}>
                                                            {user.email || user.corporateEmail || user.phone || user._id}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                                                            {tab === 0 ? 'Deleted' : 'Archived'} {tab === 0
                                                                ? (user.deletedAt ? moment(user.deletedAt).format('DD MMM YYYY') : '—')
                                                                : (user.archivedAt ? moment(user.archivedAt).format('DD MMM YYYY') : '—')}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{user.email || user.corporateEmail || '—'}</Typography>
                                                {user.phone && <Typography variant="caption" color="text.secondary">{user.phone}</Typography>}
                                            </TableCell>
                                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, whiteSpace: 'nowrap' }}>
                                                {(tab === 0 ? user.deletedAt : user.archivedAt)
                                                    ? moment(tab === 0 ? user.deletedAt : user.archivedAt).format('DD MMM YYYY, HH:mm') : '—'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                {tab === 0 ? (
                                                    <>
                                                        <Button size="small" onClick={() => setSelected(user)} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                                                            Move to archive
                                                        </Button>
                                                        <IconButton size="small" aria-label={`Archive ${user.name || user._id}`} onClick={() => setSelected(user)} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
                                                            <ArchiveOutlinedIcon />
                                                        </IconButton>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Button size="small" onClick={() => setDetailId(user._id)} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                                                            View record
                                                        </Button>
                                                        <IconButton size="small" aria-label={`View archived record for ${user.name || user._id}`} onClick={() => setDetailId(user._id)} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
                                                            <VisibilityOutlinedIcon />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {!loading && !loadError && total > pageSize && (
                        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" sx={{ mt: 2 }}>
                            <Button size="small" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</Button>
                            <Typography variant="body2" color="text.secondary">{page} of {Math.ceil(total / pageSize)}</Typography>
                            <Button size="small" disabled={page * pageSize >= total} onClick={() => setPage(page + 1)}>Next</Button>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.5 }}><Button onClick={() => setOpen(false)}>Close</Button></DialogActions>
            </Dialog>
            <Dialog open={Boolean(detailId)} onClose={() => setDetailId(null)} maxWidth="sm" fullWidth aria-labelledby="archived-user-record-title">
                <DialogTitle id="archived-user-record-title">Archived user record</DialogTitle>
                <DialogContent dividers>
                    {detailLoading ? (
                        <Stack spacing={1}><Skeleton height={32} /><Skeleton height={32} /><Skeleton height={32} /></Stack>
                    ) : detailError ? (
                        <Alert severity="error">{detailError}</Alert>
                    ) : record ? (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, overflowWrap: 'anywhere' }}>
                                Original ID: {record._id}<br />
                                Archived: {record.archivedAt ? moment(record.archivedAt).format('DD MMM YYYY, HH:mm') : '—'}
                            </Typography>
                            <TableContainer sx={{ maxHeight: 430 }}>
                                <Table size="small" aria-label="Saved user fields">
                                    <TableBody>
                                        {Object.entries(record.user || {}).map(([key, value]) => (
                                            <TableRow key={key}>
                                                <TableCell component="th" scope="row" sx={{ fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                                                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, (letter) => letter.toUpperCase())}
                                                </TableCell>
                                                <TableCell sx={{ overflowWrap: 'anywhere' }}>{formatRecordValue(value)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    ) : null}
                </DialogContent>
                <DialogActions><Button onClick={() => setDetailId(null)}>Close</Button></DialogActions>
            </Dialog>
            <Dialog open={Boolean(selected)} onClose={closeConfirmation} maxWidth="sm" fullWidth aria-labelledby="confirm-user-archive-title">
                <DialogTitle id="confirm-user-archive-title">Move user to archive?</DialogTitle>
                <DialogContent>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
                        <Avatar sx={{ bgcolor: 'grey.200', color: 'text.primary' }}>{selected?.name?.charAt(0).toUpperCase() || '?'}</Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" fontWeight={600}>{selected?.name || 'Unnamed user'}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                                {selected?.email || selected?.corporateEmail || selected?.phone || selected?._id}
                            </Typography>
                        </Box>
                    </Stack>
                    <Alert severity="warning" sx={{ mb: 2.5 }}>The user will be removed from the User collection. Their full record and original ID will remain in the archive.</Alert>
                    <Typography variant="body2" sx={{ mb: 1 }}>Copy and enter this exact phrase to confirm:</Typography>
                    <Box component="code" sx={{ display: 'block', userSelect: 'all', overflowWrap: 'anywhere', mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                        {selected ? `ARCHIVE ${selected._id}` : ''}
                    </Box>
                    <TextField
                        fullWidth
                        label="Confirmation phrase"
                        value={confirmation}
                        onChange={(event) => setConfirmation(event.target.value)}
                        error={Boolean(confirmation && selected && confirmation !== `ARCHIVE ${selected._id}`)}
                        helperText={confirmation && selected && confirmation !== `ARCHIVE ${selected._id}` ? 'Phrase must match exactly.' : 'The archive button unlocks when the phrase matches.'}
                        autoComplete="off"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeConfirmation} disabled={archiving}>Cancel</Button>
                    <Button
                        color="warning"
                        variant="contained"
                        startIcon={archiving ? <CircularProgress size={16} color="inherit" /> : <ArchiveOutlinedIcon />}
                        disabled={!selected || confirmation !== `ARCHIVE ${selected._id}` || archiving}
                        onClick={archiveUser}
                    >
                        {archiving ? 'Archiving…' : 'Move to archive'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DeletedUsers;
